import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { makeAsk, ERRORS, Card, Button, Notice, Gate, relative, Avatar, asList, TabBoundary, inputClass, selectClass } from './dashboardShared';

// #seller: your Whatnot sales and buyers, for the bot owner alone. Opened from the username menu.
// The page holds nothing: every number comes from the bot (the whatnotseller cog, through the dashboard cog), which only
// answers the bot owner's Discord login. None of it is ever written to the website's database for visitors to read.

const TABS = [['overview', 'Overview'], ['shows', 'Shows'], ['buyers', 'Buyers']];
const BAR = '#0a8fd0'; // the site's blue, darkened until it passed the chart colour checks against this dark background

const money = (amount, currency) => {
  const value = (amount || 0) / 100;
  try {
    return value.toLocaleString(undefined, { style: 'currency', currency: currency || 'GBP', minimumFractionDigits: value % 1 ? 2 : 0, maximumFractionDigits: 2 });
  } catch {
    return value.toFixed(2);
  }
};
const count = (n) => (n == null ? '—' : Number(n).toLocaleString());
const dayLabel = (day) => new Date(`${day}T12:00:00`).toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
const ORDER_STATUS = { pending: 'Awaiting payment', pending_fulfillment: 'To ship', completed: 'Completed', cancelled: 'Cancelled', failed: 'Failed' };
const ORDER_TYPE = { buy_it_now: 'Buy It Now', auction: 'Auction', giveaway: 'Giveaway', other: 'Other' };

const Tile = ({ label, value, sub }) => (
  <Card className="!p-2.5 sm:!p-4">
    <p className="text-zinc-500 text-[10px] sm:text-[11px] font-semibold uppercase tracking-wider truncate">{label}</p>
    <p className="text-white text-base sm:text-xl font-bold truncate">{value}</p>
    <p className="text-zinc-500 text-[10px] sm:text-[11px] truncate">{sub || ' '}</p>
  </Card>
);

const Warning = ({ children }) => (
  <p className="rounded-lg px-3 py-2 text-xs border border-amber-700/50 bg-amber-900/20 text-amber-200">⚠️ {children}</p>
);

// Sales for each of the last 30 days. Each whole column is the hover and tap target, so a tiny bar is still easy to reach
function SalesChart({ days, currency }) {
  const [active, setActive] = useState(null);
  const [asTable, setAsTable] = useState(false);
  const top = Math.max(...days.map(d => d.sales), 0);
  const shown = active != null ? days[active] : null;
  const total = days.reduce((sum, d) => sum + d.sales, 0);
  return (
    <Card title="Sales, last 30 days" right={<Button small tone="ghost" onClick={() => setAsTable(t => !t)}>{asTable ? 'Show chart' : 'Show table'}</Button>}>
      {asTable ? (
        <div className="max-h-64 overflow-y-auto">
          <table className="w-full text-xs">
            <thead><tr className="text-zinc-500 text-left"><th className="font-semibold py-1">Day</th><th className="font-semibold py-1 text-right">Sales</th><th className="font-semibold py-1 text-right">Orders</th></tr></thead>
            <tbody>
              {[...days].reverse().map(d => (
                <tr key={d.day} className="border-t border-zinc-800 text-zinc-300">
                  <td className="py-1">{dayLabel(d.day)}</td>
                  <td className="py-1 text-right">{money(d.sales, currency)}</td>
                  <td className="py-1 text-right">{d.orders}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <>
          <p className="text-zinc-300 text-xs mb-2 h-4 truncate" aria-live="polite">
            {shown ? `${dayLabel(shown.day)}: ${money(shown.sales, currency)} from ${shown.orders} order${shown.orders !== 1 ? 's' : ''}`
              : `${money(total, currency)} in total. Hover or tap a day for its details.`}
          </p>
          <div className="relative">
            <div className="absolute inset-x-0 top-0 border-t border-zinc-800" aria-hidden="true" />
            <div className="flex items-end h-36 border-b border-zinc-700" role="img"
              aria-label={`Daily sales for the last 30 days, ${money(total, currency)} in total. The table view lists each day.`}
              onMouseLeave={() => setActive(null)}>
              {days.map((d, i) => (
                <button key={d.day} type="button" aria-label={`${dayLabel(d.day)}: ${money(d.sales, currency)}`}
                  onMouseEnter={() => setActive(i)} onFocus={() => setActive(i)} onClick={() => setActive(i)}
                  className="flex-1 min-w-0 h-full flex items-end px-px focus:outline-none" style={{ background: active === i ? 'rgba(255,255,255,0.05)' : 'transparent' }}>
                  <span className="block w-full rounded-t" style={{ height: d.sales && top ? `${Math.max(3, (d.sales / top) * 100)}%` : 0, background: BAR }} />
                </button>
              ))}
            </div>
          </div>
          <div className="flex justify-between text-zinc-500 text-[10px] mt-1">
            <span>{dayLabel(days[0].day)}</span>
            <span>Best day: {money(top, currency)}</span>
            <span>Today</span>
          </div>
        </>
      )}
    </Card>
  );
}

function OverviewTab({ ask, failed }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    ask('seller.overview').then((answer) => { if (!failed(answer)) setData(answer.data); });
  }, [ask, failed]);
  if (!data) return <p className="text-zinc-500 text-xs">Loading…</p>;
  const days = asList(data.days);
  const recent = asList(data.recent);
  const state = data.state || {};
  const problems = asList(state.problems);
  const { today = {}, week = {}, month = {}, all = {} } = data.periods || {};
  return (
    <div className="space-y-4">
      {state.scanning && <Warning>Still reading your order history from Whatnot, so these totals are still going up. Refresh in a little while.</Warning>}
      {!state.scanning && !state.ordersKnown && <p className="text-zinc-500 text-xs">No orders read yet. They'll appear here within a minute or so of the token being set up.</p>}
      {problems.map((p) => <Warning key={p}>{p}</Warning>)}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-3">
        {[['Today', today], ['7 days', week], ['30 days', month], ['All time', all]].map(([label, t]) => (
          <Tile key={label} label={label} value={money(t.sales, data.currency)} sub={`${count(t.orders || 0)} order${t.orders !== 1 ? 's' : ''} · ${count(t.buyers || 0)} buyer${t.buyers !== 1 ? 's' : ''}`} />
        ))}
      </div>
      <p className="text-zinc-500 text-[11px]">
        <span className="text-white font-semibold">{count(data.toShip || 0)}</span> to ship. Sales are the price of paid items, before shipping, tax and fees. Giveaways and cancelled orders aren't counted.
      </p>
      {days.length > 0 && <SalesChart days={days} currency={data.currency} />}
      <Card title="Latest orders">
        {recent.length === 0 ? <p className="text-zinc-500 text-xs">Nothing yet.</p> : (
          <ul className="divide-y divide-zinc-800">
            {recent.map((o) => (
              <li key={o.id} className="flex items-center gap-2 py-2">
                <div className="min-w-0 flex-1">
                  <p className="text-white text-xs font-semibold truncate">{o.buyer ? `@${o.buyer}` : 'Buyer not available'}</p>
                  <p className="text-zinc-500 text-[11px] truncate">{ORDER_TYPE[o.type] || 'Order'} · {ORDER_STATUS[o.status] || o.status} · {o.at ? relative(o.at) : ''}</p>
                </div>
                <span className="text-white text-xs font-semibold flex-shrink-0">{money(o.amount, o.currency)}</span>
              </li>
            ))}
          </ul>
        )}
      </Card>
    </div>
  );
}

const SHOW_WAITING = {
  NOT_ENDED: "Whatnot hasn't finished this show.",
  PENDING: "Whatnot doesn't have stats for this show yet.",
  WILL_NOT_BE_CALCULATED: "Whatnot doesn't make stats for private, rehearsal or cancelled shows.",
  UNAVAILABLE: "Couldn't load this show's stats just now.",
};

function ShowsTab({ ask, failed }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    ask('seller.shows').then((answer) => { if (!failed(answer)) setData(answer.data); });
  }, [ask, failed]);
  if (!data) return <p className="text-zinc-500 text-xs">Loading…</p>;
  const upcoming = asList(data.upcoming);
  const past = asList(data.past);
  return (
    <div className="space-y-4">
      <Card title="Coming up">
        {upcoming.length === 0 ? <p className="text-zinc-500 text-xs">No shows scheduled.</p> : (
          <ul className="divide-y divide-zinc-800">
            {upcoming.map((s) => (
              <li key={s.id} className="py-2 flex items-center gap-2">
                <p className="text-white text-xs font-semibold flex-1 min-w-0 truncate">{(s.title || 'Untitled').replace(/\s*\n\s*/g, ' ')}</p>
                {s.visibility === 'private' && <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-zinc-700 text-zinc-200">Private</span>}
                <span className="text-zinc-500 text-[11px] flex-shrink-0">{s.at ? new Date(s.at).toLocaleString([], { weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : 'No time set'}</span>
              </li>
            ))}
          </ul>
        )}
      </Card>
      <Card title="Past shows">
        {past.length === 0 ? <p className="text-zinc-500 text-xs">No past shows yet.</p> : (
          <ul className="divide-y divide-zinc-800">
            {past.map((s) => (
              <li key={s.id} className="py-3">
                <div className="flex items-baseline justify-between gap-2">
                  <p className="text-white text-xs font-semibold min-w-0 truncate">{(s.title || 'Untitled').replace(/\s*\n\s*/g, ' ')}</p>
                  <span className="text-zinc-500 text-[11px] flex-shrink-0">{s.at ? new Date(s.at).toLocaleDateString() : ''}</span>
                </div>
                {s.status !== 'GENERATED' ? <p className="text-zinc-500 text-[11px] mt-1">{SHOW_WAITING[s.status] || 'No stats for this show.'}</p> : (
                  <dl className="grid grid-cols-3 sm:grid-cols-6 gap-x-3 gap-y-2 mt-2">
                    {[['Sales', money(s.sales, s.currency)], ['Earnings (est.)', money(s.earnings, s.currency)], ['Orders', count(s.orders)],
                      ['Buyers', s.buyers == null ? '—' : `${count(s.buyers)}${s.firstTime != null ? ` (${count(s.firstTime)} new)` : ''}`],
                      ['Peak viewers', count(s.peak)], ['Rating', s.rating != null ? Number(s.rating).toFixed(1) : '—']].map(([label, value]) => (
                      <div key={label} className="min-w-0">
                        <dt className="text-zinc-500 text-[10px] truncate">{label}</dt>
                        <dd className="text-white text-xs font-semibold truncate">{value}</dd>
                      </div>
                    ))}
                  </dl>
                )}
              </li>
            ))}
          </ul>
        )}
      </Card>
    </div>
  );
}

function BuyersTab({ ask, failed, setNote }) {
  const [data, setData] = useState(null);
  const [roleId, setRoleId] = useState('');
  const [minSpend, setMinSpend] = useState('');
  const [busy, setBusy] = useState('');
  const [confirm, setConfirm] = useState('');
  const [search, setSearch] = useState('');

  const load = useCallback(async () => {
    const answer = await ask('seller.buyers');
    if (!failed(answer)) setData(answer.data);
  }, [ask, failed]);
  useEffect(() => { load(); }, [load]);

  const act = async (action, args) => {
    setBusy(action); setConfirm(''); setNote(null);
    const answer = await ask(action, args);
    setBusy('');
    if (failed(answer)) return;
    setNote({ text: `✅ ${answer.data.message}`, ok: true });
    load();
  };

  const buyers = useMemo(() => asList(data?.buyers), [data]);
  if (!data) return <p className="text-zinc-500 text-xs">Loading…</p>;
  const rules = asList(data.rules);
  const roles = asList(data.roles);
  const need = Math.round((Number(minSpend) || 0) * 100); // in pence, as the bot has it
  const ready = roleId && need >= 100;
  const qualify = buyers.filter(b => b.member && b.spent >= need).length;
  const words = search.toLowerCase().trim();
  const shown = words ? buyers.filter(b => b.username.includes(words) || (b.member?.name || '').toLowerCase().includes(words)) : buyers;

  return (
    <div className="space-y-4">
      {data.customers === false && (
        <Warning>Your orders are arriving without their buyers, so nobody can be matched. Add the read:customers scope to your Whatnot token, then restart the bot.</Warning>
      )}
      {data.scanning && <Warning>Still reading your order history, so some buyers may not be here yet. Roles are only given for now, never taken back, until it's finished.</Warning>}

      <div className="grid grid-cols-2 gap-2 sm:gap-3">
        <Tile label="Buyers" value={count(data.totalBuyers)} sub="have paid for something" />
        <Tile label="In the server" value={count(data.matched)} sub="verified with their username" />
      </div>

      <Card title="Buyer roles">
        <p className="text-zinc-400 text-xs mb-3">
          TOGΞ gives these roles by itself once someone's total spend reaches the amount. It matches each order's buyer to
          the Whatnot username a member verified with. Spend is the price of paid items, before shipping and tax. Giveaway
          wins, unpaid and cancelled orders don't count. It only ever takes back a role that it gave, if cancelled orders
          take them back under the amount.
        </p>
        <div className="flex flex-wrap items-end gap-2">
          <label className="flex-1 min-w-[10rem]">
            <span className="block text-zinc-500 text-[11px] mb-1">Role</span>
            <select value={roleId} onChange={(e) => { setRoleId(e.target.value); setConfirm(''); }} className={`${selectClass} w-full`}>
              <option value="">Choose a role…</option>
              {roles.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
            </select>
          </label>
          <label className="w-28">
            <span className="block text-zinc-500 text-[11px] mb-1">Spent (£)</span>
            <input type="number" min="1" step="any" inputMode="decimal" placeholder="e.g. 100" value={minSpend} onChange={(e) => { setMinSpend(e.target.value); setConfirm(''); }} className={inputClass} />
          </label>
          <Button tone={confirm === 'add' ? 'success' : 'primary'} disabled={!ready || !!busy}
            onClick={() => (confirm === 'add' ? act('seller.rule.add', { roleId, minSpend: String(minSpend) }) : setConfirm('add'))}>
            {busy === 'seller.rule.add' ? '…' : confirm === 'add' ? `Yes, give it to ${count(qualify)}` : 'Add rule'}
          </Button>
        </div>
        {ready && <p className="text-zinc-400 text-[11px] mt-2">{count(qualify)} member{qualify !== 1 ? 's' : ''} in the server qualif{qualify !== 1 ? 'y' : 'ies'} right now, and more will get it as they buy or verify.</p>}
        <p className="text-zinc-500 text-[11px] mt-2">Only roles TOGΞ can give and without moderator or admin permissions are listed.</p>

        {rules.length > 0 && (
          <ul className="divide-y divide-zinc-800 mt-3 border-t border-zinc-800">
            {rules.map(r => (
              <li key={r.roleId} className="flex items-center gap-2 py-2">
                <div className="min-w-0 flex-1">
                  <p className="text-white text-xs font-semibold truncate">{r.name}</p>
                  <p className="text-zinc-500 text-[11px]">{r.minSpend != null ? `${money(r.minSpend, data.currency)}+ spent` : `${r.minOrders}+ order${r.minOrders !== 1 ? 's' : ''}`} · {count(r.holders)} ha{r.holders !== 1 ? 've' : 's'} it</p>
                </div>
                <Button small tone={confirm === r.roleId ? 'danger' : 'ghost'} disabled={!!busy}
                  onClick={() => (confirm === r.roleId ? act('seller.rule.remove', { roleId: r.roleId }) : setConfirm(r.roleId))}>
                  {confirm === r.roleId ? 'Sure?' : 'Remove'}
                </Button>
              </li>
            ))}
          </ul>
        )}
        {rules.length > 0 && (
          <div className="mt-3">
            <Button small disabled={!!busy} onClick={() => act('seller.sync')}>{busy === 'seller.sync' ? 'Updating…' : '🔄 Update roles now'}</Button>
          </div>
        )}
      </Card>

      <Card title={`Top buyers${data.totalBuyers > buyers.length ? ` · top ${buyers.length} of ${count(data.totalBuyers)}` : ''}`}>
        {buyers.length === 0 ? <p className="text-zinc-500 text-xs">No buyers yet.</p> : (
          <>
            <input type="text" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search by username…" aria-label="Search buyers" className={`${inputClass} mb-2`} />
            {shown.length === 0 ? <p className="text-zinc-500 text-xs py-2">Nobody matches that.</p> : (
              <ul className="divide-y divide-zinc-800">
                {shown.map(b => (
                  <li key={b.username} className="flex items-center gap-2 py-2">
                    <Avatar src={b.member?.avatar} name={b.username} size={28} />
                    <div className="min-w-0 flex-1">
                      <a href={`https://www.whatnot.com/user/${encodeURIComponent(b.username)}`} target="_blank" rel="noreferrer" className="text-white text-xs font-semibold truncate block hover:underline">@{b.username}</a>
                      <p className="text-zinc-500 text-[11px] truncate">
                        {count(b.orders)} order{b.orders !== 1 ? 's' : ''} · last {b.last ? relative(b.last) : '—'}{b.member ? ` · ${b.member.name} in the server` : ' · not in the server'}
                      </p>
                    </div>
                    <span className="text-white text-xs font-semibold flex-shrink-0">{money(b.spent, data.currency)}</span>
                  </li>
                ))}
              </ul>
            )}
          </>
        )}
      </Card>
    </div>
  );
}

export default function SellerDashboard({ database, discordUser, isAdmin, getToken, onLogin }) {
  // The same `ask` for the life of the page, as on the TOGΞ Dashboard (the site redraws every second for its clock)
  const tokenRef = useRef(getToken);
  tokenRef.current = getToken;
  const ask = useMemo(() => makeAsk(database, () => tokenRef.current()), [database]);
  const [tab, setTab] = useState('overview');
  const [problem, setProblem] = useState(null); // a key of ERRORS
  const [note, setNote] = useState(null);
  const [reload, setReload] = useState(0);

  const failed = useCallback((answer) => {
    if (answer.ok) return false;
    if (ERRORS[answer.error]) setProblem(answer.error);
    else setNote({ text: answer.error, ok: false });
    return true;
  }, []);

  if (!discordUser) return <Gate title="Seller Dashboard" text="Log in with Discord to use the dashboard." onLogin={onLogin} />;
  if (!isAdmin || problem === 'forbidden') return <Gate title="Seller Dashboard" text={ERRORS.forbidden} />;
  if (problem === 'login' || !getToken()) return <Gate title="Seller Dashboard" text={ERRORS.login} onLogin={onLogin} />;

  const shared = { ask, failed, setNote };
  return (
    <main className="flex-1">
      <div className="max-w-3xl mx-auto px-4 pt-2 pb-10">
        <div className="flex items-center gap-3 mb-4">
          <img src="/wnlogo.png" alt="" className="h-10 w-10 rounded-xl object-contain" />
          <div className="min-w-0 flex-1">
            <h1 className="text-white text-lg font-bold leading-tight">Seller Dashboard</h1>
            <p className="text-zinc-400 text-[11px] truncate">{problem ? ERRORS[problem] : 'Private: only you can see this'}</p>
          </div>
          <Button tone="ghost" small onClick={() => { setNote(null); setProblem(null); setReload(r => r + 1); }}>↻ Refresh</Button>
        </div>

        <div className="mb-4 -mx-4 px-4 overflow-x-auto">
          <div className="flex gap-1 p-1 rounded-lg bg-zinc-900 border border-zinc-800 w-max">
            {TABS.map(([id, label]) => (
              <button key={id} onClick={() => { setTab(id); setNote(null); }}
                className={`px-3 py-1 rounded-md text-xs font-semibold transition-colors whitespace-nowrap ${tab === id ? 'bg-zinc-700 text-white' : 'text-zinc-400 hover:text-white'}`}>
                {label}
              </button>
            ))}
          </div>
        </div>

        <Notice note={note} />

        <TabBoundary key={`${tab}-${reload}`}>
          {tab === 'overview' && <OverviewTab {...shared} />}
          {tab === 'shows' && <ShowsTab {...shared} />}
          {tab === 'buyers' && <BuyersTab {...shared} />}
        </TabBoundary>
      </div>
    </main>
  );
}
